import { Button, Flex, Text, Radio, Group } from '@mantine/core';
import { closeModal, openModal } from '@mantine/modals';
import classes from './useDataExport.module.scss';
import { MODAL_SIZE_MEDIUM } from '@shared/ui/modal/constants';
import { useForm, zodResolver } from '@mantine/form';
import { IconCalendar } from '@tabler/icons-react';
import { DateInput } from '@mantine/dates';
import {
  saveFile,
  getFilterStartOfDay,
  getFilterEndOfDay,
} from '@shared/helpers';
import type { FC } from 'react';
import {
  $DataExportSchema,
  type TModalVariants,
  type UseDataExportComponentProps,
  type UseDataExportProps,
} from './types';

const UseDataExportComponent: FC<UseDataExportComponentProps> = ({
  MODAL_ID,
  text,
  type,
  isPending,
  exportData,
}) => {
  const form = useForm({
    initialValues: {
      periodType: 'all' as 'all' | 'range',
      startDate: null as Date | null,
      endDate: null as Date | null,
    },
    validate: zodResolver($DataExportSchema),
  });

  const handleSubmit = async (values: typeof form.values) => {
    if (values.periodType === 'range') {
      if (!values.startDate) {
        form.setFieldError('startDate', 'Укажите начальную дату');
        return;
      }

      if (!values.endDate) {
        form.setFieldError('endDate', 'Укажите конечную дату');
        return;
      }

      if (values.endDate < values.startDate) {
        form.setFieldError(
          'endDate',
          'Конечная дата не может быть раньше начальной'
        );
        return;
      }
    }

    try {
      const dateFrom =
        values.periodType === 'all'
          ? null
          : values.startDate
            ? getFilterStartOfDay(values.startDate)
            : null;

      const dateTo =
        values.periodType === 'all'
          ? null
          : values.endDate
            ? getFilterEndOfDay(values.endDate)
            : null;

      const response = await exportData({
        dateFrom,
        dateTo,
      });

      const blob = response.data;

      if (!(blob instanceof Blob)) {
        throw new Error('Invalid response format');
      }

      if (blob.size === 0) {
        throw new Error('Получен пустой файл');
      }

      const filename =
        response.filename ||
        `${type}_export_${new Date().toISOString().split('T')[0]}.xlsx`;

      saveFile(blob, filename);
      closeModal(MODAL_ID);
    } catch {
      closeModal(MODAL_ID);
    }
  };

  const handleCancel = () => {
    closeModal(MODAL_ID);
  };

  const handlePeriodTypeChange = (value: string) => {
    const newPeriodType = value as 'all' | 'range';
    form.setFieldValue('periodType', newPeriodType);

    if (newPeriodType === 'all') {
      form.setFieldValue('startDate', null);
      form.setFieldValue('endDate', null);
    }
  };

  return (
    <Flex w='100%' flex={1}>
      <form className={classes.form} onSubmit={form.onSubmit(handleSubmit)}>
        <Flex direction='column' gap={16} pb={16}>
          <Text c='tertiary.7' size='sm'>
            {text}
          </Text>

          <Radio.Group
            value={form.values.periodType}
            onChange={handlePeriodTypeChange}
          >
            <Group mt='xs' gap='1rem' justify='space-around'>
              <Radio value='all' size='md' label='За все время' />
              <Radio value='range' size='md' label='За период времени' />
            </Group>
          </Radio.Group>

          <Flex gap={16} direction={{ base: 'column', sm: 'row' }}>
            <DateInput
              placeholder='c'
              size='md'
              valueFormat='DD.MM.YYYY'
              popoverProps={{ withinPortal: true }}
              leftSection={<IconCalendar size={16} />}
              value={form.values.startDate}
              key={form.key('startDate')}
              {...form.getInputProps('startDate')}
              maxDate={form.values.endDate || undefined}
              error={form.errors.startDate}
              disabled={form.values.periodType === 'all'}
              clearable
            />

            <DateInput
              size='md'
              placeholder='до'
              valueFormat='DD.MM.YYYY'
              popoverProps={{ withinPortal: true }}
              leftSection={<IconCalendar size={16} />}
              value={form.values.endDate}
              key={form.key('endDate')}
              {...form.getInputProps('endDate')}
              minDate={form.values.startDate || undefined}
              error={form.errors.endDate}
              disabled={form.values.periodType === 'all'}
              clearable
            />
          </Flex>
        </Flex>

        <Flex gap={8} mt={12} justify='flex-end'>
          <Button variant='gray' onClick={handleCancel}>
            Отмена
          </Button>
          <Button
            type='submit'
            loading={isPending}
            disabled={
              form.values.periodType === 'range' &&
              (!form.values.startDate || !form.values.endDate)
            }
          >
            Экспортировать
          </Button>
        </Flex>
      </form>
    </Flex>
  );
};

export const useDataExport = ({
  type,
  title,
  text,
  isPending,
  exportData,
}: UseDataExportProps) => {
  const modalId: Record<TModalVariants, string> = {
    vacancy: 'vacancy-modal-export',
    users: 'user-modal-export',
    candidate: 'candidate-modal-export',
    company: 'company-modal-export',
  };
  const openChangeDataExport = () =>
    openModal({
      modalId: modalId[type],
      title,
      size: MODAL_SIZE_MEDIUM,
      children: (
        <UseDataExportComponent
          MODAL_ID={modalId[type]}
          type={type}
          text={text}
          isPending={isPending}
          exportData={exportData}
        />
      ),
    });

  return { openChangeDataExport };
};
